import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { RouteComponentProps } from 'react-router-dom';
import { ReportsService } from 'services';
import { DatePicker, KPICard, ChartCard } from 'components';
import { METRICS_GLOSSARY } from './metrics-glossary';
import ModalGlossary from './modal-glossary';
import ModalSubusersDetail from './modal-subusers-detail';
import { exportToCSV, exportToExcel } from './export-utils';
import moment from 'moment';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
	Chart,
	BarController,
	BarElement,
	LineController,
	LineElement,
	PointElement,
	LinearScale,
	CategoryScale,
	ArcElement,
	Filler,
	Tooltip as ChartTooltip,
	Legend,
	DoughnutController
} from 'chart.js';

Chart.register(
	BarController,
	BarElement,
	LineController,
	LineElement,
	PointElement,
	LinearScale,
	CategoryScale,
	ArcElement,
	Filler,
	ChartTooltip,
	Legend,
	ChartDataLabels,
	DoughnutController
);

interface Props extends RootState, RouteComponentProps {
	dispatch: any
}

type SectionKey = 'users' | 'patrimony' | 'plans' | 'chat' | 'activity';

// ---------- Helpers defensivos (mismos que /admin/home) ----------

const safeNum = (value: any): number => {
	const n = Number(value);
	return isNaN(n) ? 0 : n;
}

const formatMoney = (value: any): string => {
	const n = Number(value) || 0;
	if (Math.abs(n) >= 1000000) return `$${ (n / 1000000).toFixed(1) }M`;
	if (Math.abs(n) >= 1000) return `$${ (n / 1000).toFixed(1) }K`;
	return `$${ n.toLocaleString('en-US', { maximumFractionDigits: 0 }) }`;
}

const formatPct = (value: any): string => {
	const n = Number(value);
	return `${ (isNaN(n) ? 0 : n).toFixed(2) }%`;
}

// Paleta Holdz para charts
const COLORS = {
	free: '#54aa3f',     // verde principal
	premium: '#38e788',  // verde brillante
	orange: '#ef6526',   // advertencia / ghost / expired
	danger: '#fb0200',
	blue: '#4d8eff',     // variedad 4+ segmentos
	muted: 'rgba(255,255,255,0.12)',
}

// Pills -> period que entiende el backend (mismo patrón que /admin/home)
const PERIODS = [
	{ key: 'today', label: 'Hoy' },
	{ key: 'week', label: 'Semana' },
	{ key: 'month', label: 'Mes' },
	{ key: 'year', label: 'Año' },
	{ key: 'all', label: 'Todo' },
	{ key: 'custom', label: 'Custom' },
];

// Atajos rápidos del rango custom -> devuelven { since, until } como Date
const QUICK_RANGES = [
	{
		key: 'last7', label: 'Últimos 7 días',
		range: () => ({ since: moment().subtract(6, 'days').startOf('day').toDate(), until: moment().endOf('day').toDate() })
	},
	{
		key: 'last30', label: 'Últimos 30 días',
		range: () => ({ since: moment().subtract(29, 'days').startOf('day').toDate(), until: moment().endOf('day').toDate() })
	},
	{
		key: 'prevMonth', label: 'Mes anterior',
		range: () => ({
			since: moment().subtract(1, 'month').startOf('month').toDate(),
			until: moment().subtract(1, 'month').endOf('month').toDate()
		})
	},
	{
		key: 'currMonth', label: 'Mes actual',
		range: () => ({ since: moment().startOf('month').toDate(), until: moment().endOf('day').toDate() })
	},
	{
		key: 'currYear', label: 'Año actual',
		range: () => ({ since: moment().startOf('year').toDate(), until: moment().endOf('day').toDate() })
	},
] as const;

const TABS: { key: SectionKey; label: string }[] = [
	{ key: 'users', label: 'Usuarios' },
	{ key: 'patrimony', label: 'Patrimonio' },
	{ key: 'plans', label: 'Planes' },
	{ key: 'chat', label: 'Chat IA' },
	{ key: 'activity', label: 'Actividad' },
];

const EMPTY_SECTION_DATA = {
	users: null as any,
	patrimony: null as any,
	plans: null as any,
	chat: null as any,
	activity: null as any,
};

// Devuelve la descripción corta de una métrica desde el glosario
const metricDesc = (section: string, key: string): string => {
	const def = (METRICS_GLOSSARY[section] || []).find((d) => d.key === key);
	return def?.description || '';
}

// Icono "?" con tooltip nativo (title). Helper inline, no componente global.
const MetricHelp = ({ description }: { description: string }) => {
	if (!description) return null;
	return (
		<span className="metric-help" title={ description } aria-label={ description }>?</span>
	);
}

class Reports extends React.Component<Props> {

	popoverRef: HTMLDivElement | null = null;
	exportRef: HTMLDivElement | null = null;
	// Charts como propiedades de instancia (NO en state) — mismo patrón que home.
	// destroyCharts() en unmount + antes de cada recreación para evitar memory leaks.
	charts: Record<string, any> = {};

	state = {
		// Filtro global (mismo patrón que home)
		period: 'month',
		since: null as Date | null,
		until: null as Date | null,
		activeQuickRange: '' as string,
		// popover flotante del rango custom
		customPopoverOpen: false,
		draftSince: null as Date | null,
		draftUntil: null as Date | null,
		draftQuickRange: '' as string,

		// Tab activo
		activeTab: 'users' as SectionKey,

		// Data por tab (cache: si ya cargó un tab, no recargar al volver)
		sectionData: { ...EMPTY_SECTION_DATA },

		// Loading individual por tab
		loadingTabs: [] as SectionKey[],

		// Errores por tab
		errorTabs: {} as Record<string, string>,

		// Modal glosario
		glossaryModalVisible: false,
		glossaryModalSection: 'users' as SectionKey,

		// Modal drilldown subusuarios
		subusersModalVisible: false,
		subusersModalParentId: null as number | null,
		subusersModalParentName: '' as string,

		// Dropdown de exportación
		exportDropdownOpen: false,
	}

	componentDidMount() {
		this.props.dispatch({
			type: 'SET_HEADER',
			payload: {
				title: 'Reportes',
				onBack: null
			}
		});

		document.addEventListener('mousedown', this.handleClickOutside);
		document.addEventListener('keydown', this.handleEscape);

		// Al montar: cargar el tab por default ('users')
		this.loadTab(this.state.activeTab);
	}

	componentDidUpdate(_prevProps: Props, prevState: any) {
		const { activeTab, sectionData } = this.state;
		const tabChanged = prevState.activeTab !== activeTab;
		const dataArrived = prevState.sectionData[activeTab] == null && sectionData[activeTab] != null;

		// Recrear charts SOLO si cambió el tab o si recién llegó la data del activo
		if (tabChanged || dataArrived) {
			this.renderActiveCharts();
		}
	}

	componentWillUnmount() {
		this.destroyCharts();
		document.removeEventListener('mousedown', this.handleClickOutside);
		document.removeEventListener('keydown', this.handleEscape);
	}

	destroyCharts = () => {
		Object.keys(this.charts).forEach((k) => {
			try { this.charts[k]?.destroy(); } catch (e) { /* noop */ }
		});
		this.charts = {};
	}

	// Recrea los charts del tab activo (si tiene data y canvas montado)
	renderActiveCharts = () => {
		const { activeTab, sectionData } = this.state;
		const data: any = sectionData[activeTab];
		if (!data) return;

		const m = data.metrics || {};

		try {
			if (activeTab === 'patrimony') {
				this.buildDoughnut('chart-patrimony-adoption', {
					labels: ['Con patrimonio', 'Sin patrimonio'],
					values: [
						safeNum(m.patrimony_adoption?.with_patrimony),
						Math.max(
							safeNum(m.patrimony_adoption?.total_clients) - safeNum(m.patrimony_adoption?.with_patrimony),
							0
						)
					],
					colors: [COLORS.premium, COLORS.muted],
				});
			}
			else if (activeTab === 'plans') {
				const byMonth = Array.isArray(m.revenue?.by_month) ? m.revenue.by_month : [];
				this.buildLine('chart-revenue', {
					labels: byMonth.map((r: any) => r?.ym ?? ''),
					values: byMonth.map((r: any) => safeNum(r?.revenue)),
				});
				const f = m.funnel || {};
				this.buildHBar('chart-funnel', {
					labels: ['Registrados', 'Activos 30d', 'Premium activos'],
					values: [
						safeNum(f.registered),
						safeNum(f.active_30d),
						safeNum(f.premium_active)
					],
					colors: [COLORS.free, COLORS.blue, COLORS.premium],
				});
			}
			else if (activeTab === 'chat') {
				this.buildDoughnut('chart-chat-adoption', {
					labels: ['Usaron chat', 'Nunca usaron'],
					values: [
						safeNum(m.chat_adoption?.ever_used),
						Math.max(
							safeNum(m.chat_adoption?.total_clients) - safeNum(m.chat_adoption?.ever_used),
							0
						)
					],
					colors: [COLORS.premium, COLORS.muted],
				});
			}
			else if (activeTab === 'activity') {
				const feats = Array.isArray(m.feature_adoption?.features) ? m.feature_adoption.features : [];
				this.buildHBar('chart-feature-adoption', {
					labels: feats.map((x: any) => x?.name ?? ''),
					values: feats.map((x: any) => safeNum(x?.users)),
					colors: feats.map((_: any, i: number) =>
						[COLORS.free, COLORS.premium, COLORS.blue, COLORS.orange][i % 4]
					),
				});
			}
		}
		catch (e) {
			// nunca crashear el render por chart.js
			// eslint-disable-next-line no-console
			console.log(e);
		}
	}

	buildDoughnut = (id: string, cfg: { labels: string[]; values: number[]; colors: string[] }) => {
		const el: any = document.getElementById(id);
		if (!el) return;
		this.charts[id] = new Chart(el, {
			type: 'doughnut',
			data: {
				labels: cfg.labels,
				datasets: [{
					data: cfg.values,
					backgroundColor: cfg.colors,
					borderWidth: 4,
					borderColor: '#171a1f',
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				cutout: '64%',
				plugins: {
					datalabels: { display: false },
					legend: { display: false }
				}
			}
		});
	}

	buildLine = (id: string, cfg: { labels: string[]; values: number[] }) => {
		const el: any = document.getElementById(id);
		if (!el) return;
		this.charts[id] = new Chart(el, {
			type: 'line',
			data: {
				labels: cfg.labels,
				datasets: [{
					label: 'Revenue',
					data: cfg.values,
					borderColor: COLORS.free,
					backgroundColor: 'rgba(84, 170, 63, 0.18)',
					fill: true,
					tension: 0.35,
					pointRadius: 2,
					pointBackgroundColor: COLORS.premium,
					borderWidth: 2,
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					datalabels: { display: false },
					legend: { display: false }
				},
				scales: {
					x: {
						grid: { display: false },
						ticks: { color: 'rgba(255,255,255,0.45)', maxTicksLimit: 8, font: { size: 10 } }
					},
					y: {
						beginAtZero: true,
						grid: { color: 'rgba(255,255,255,0.05)' },
						ticks: { color: 'rgba(255,255,255,0.45)', precision: 0, font: { size: 10 } }
					}
				}
			}
		});
	}

	buildHBar = (id: string, cfg: { labels: string[]; values: number[]; colors: string[] }) => {
		const el: any = document.getElementById(id);
		if (!el) return;
		this.charts[id] = new Chart(el, {
			type: 'bar',
			data: {
				labels: cfg.labels,
				datasets: [{
					label: 'Total',
					data: cfg.values,
					backgroundColor: cfg.colors,
					borderRadius: 6,
					maxBarThickness: 36,
				}]
			},
			options: {
				indexAxis: 'y',
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					datalabels: {
						color: '#fff',
						anchor: 'end',
						align: 'end',
						font: { weight: 'bold', size: 12 },
						formatter: (v: any) => v
					},
					legend: { display: false }
				},
				scales: {
					x: { display: false, beginAtZero: true, grace: '15%' },
					y: {
						grid: { display: false },
						ticks: { color: 'rgba(255,255,255,0.7)', font: { size: 11 } }
					}
				}
			}
		});
	}

	handleClickOutside = (e: MouseEvent) => {
		if (
			this.state.customPopoverOpen &&
			this.popoverRef &&
			!this.popoverRef.contains(e.target as Node)
		) {
			this.closeCustomPopover();
		}

		if (
			this.state.exportDropdownOpen &&
			this.exportRef &&
			!this.exportRef.contains(e.target as Node)
		) {
			this.closeExportDropdown();
		}
	}

	handleEscape = (e: KeyboardEvent) => {
		if (e.key === 'Escape' && this.state.customPopoverOpen) {
			this.closeCustomPopover();
		}
	}

	buildParams = () => {
		const { period, since, until } = this.state;
		const params: any = { period, withoutLoading: true };

		if (period === 'custom') {
			if (since) params.since = moment(since).format('YYYY-MM-DD');
			if (until) params.until = moment(until).format('YYYY-MM-DD');
		}

		return params;
	}

	// Lazy load del tab: si ya está en cache no re-fetchea (salvo force)
	loadTab = async (tab: SectionKey, force = false) => {
		if (!force && this.state.sectionData[tab] != null) {
			return;
		}

		this.setState((prev: any) => ({
			loadingTabs: prev.loadingTabs.includes(tab)
				? prev.loadingTabs
				: [...prev.loadingTabs, tab],
			errorTabs: { ...prev.errorTabs, [tab]: '' },
		}));

		let res: any = null;
		let errorMsg = '';

		try {
			res = await ReportsService.admin.get(tab, this.buildParams());
		}
		catch (e: any) {
			errorMsg = e?.message || 'No se pudo cargar la información';
		}

		this.setState((prev: any) => ({
			sectionData: { ...prev.sectionData, [tab]: errorMsg ? null : (res || {}) },
			loadingTabs: prev.loadingTabs.filter((t: SectionKey) => t !== tab),
			errorTabs: { ...prev.errorTabs, [tab]: errorMsg },
		}));
	}

	setActiveTab = (tab: SectionKey) => {
		if (tab === this.state.activeTab) return;
		// Destruir charts del tab anterior ANTES de cambiar (evita memory leak)
		this.destroyCharts();
		// Cambia de tab -> muestra cache si existe, si no hace lazy load.
		// Cerrar el dropdown de export al cambiar de tab (edge case 5).
		this.setState({ activeTab: tab, exportDropdownOpen: false }, () => this.loadTab(tab));
	}

	// Al cambiar el filtro temporal: invalida TODA la cache y recarga
	// SOLO el tab activo. Los demás cargan bajo demanda al hacer click.
	invalidateAndReloadActive = () => {
		// La data va a cambiar -> destruir charts vigentes; se recrean al volver data
		this.destroyCharts();
		this.setState({
			sectionData: { ...EMPTY_SECTION_DATA },
			errorTabs: {},
		}, () => this.loadTab(this.state.activeTab, true));
	}

	setPeriod = (period: string) => {
		if (period === 'custom') {
			// Abre el popover flotante; siembra los drafts con lo ya aplicado
			this.setState({
				customPopoverOpen: true,
				draftSince: this.state.since,
				draftUntil: this.state.until,
				draftQuickRange: this.state.activeQuickRange,
			});
			return;
		}

		// Otro pill -> cierra el popover, cambia el filtro, invalida cache
		this.setState({ period, customPopoverOpen: false }, () => {
			this.invalidateAndReloadActive();
		});
	}

	closeCustomPopover = () => {
		// Cierra descartando los drafts (no aplica)
		this.setState({ customPopoverOpen: false });
	}

	onChangeDraftDate = (key: 'draftSince' | 'draftUntil', date: Date | null) => {
		this.setState({ [key]: date, draftQuickRange: '' } as any);
	}

	setDraftQuickRange = (key: string) => {
		const preset = QUICK_RANGES.find((q) => q.key === key);
		if (!preset) return;
		const { since, until } = preset.range();
		this.setState({ draftSince: since, draftUntil: until, draftQuickRange: key });
	}

	applyCustomRange = () => {
		const { draftSince, draftUntil, draftQuickRange } = this.state;
		this.setState({
			period: 'custom',
			since: draftSince,
			until: draftUntil,
			activeQuickRange: draftQuickRange,
			customPopoverOpen: false,
		}, () => {
			if (this.state.since && this.state.until) {
				this.invalidateAndReloadActive();
			}
		});
	}

	// Tabla compacta reutilizable. cols: [{ header, render(row), align? }]
	renderMiniTable = (
		rows: any[],
		cols: { header: string; render: (row: any, i: number) => React.ReactNode; align?: 'right' }[]
	) => {
		const list = Array.isArray(rows) ? rows : [];
		return (
			<div className="reports-table-wrap">
				<table className="reports-table">
					<thead>
						<tr>
							{ cols.map((c, i) => (
								<th key={ i } className={ c.align === 'right' ? 'text-right' : '' }>{ c.header }</th>
							)) }
						</tr>
					</thead>
					<tbody>
						{
							list.length > 0 ? (
								list.map((row, ri) => (
									<tr key={ ri }>
										{ cols.map((c, ci) => (
											<td key={ ci } className={ c.align === 'right' ? 'text-right' : '' }>
												{ c.render(row, ri) }
											</td>
										)) }
									</tr>
								))
							) : (
								<tr>
									<td colSpan={ cols.length } className="no-items">No se encontraron registros</td>
								</tr>
							)
						}
					</tbody>
				</table>
			</div>
		)
	}

	fullName = (u: any) => [u?.name, u?.lastname].filter(Boolean).join(' ').trim() || '—';

	// ---------- Glosario + drilldown subusuarios ----------

	openGlossary = () => this.setState({
		glossaryModalVisible: true,
		glossaryModalSection: this.state.activeTab,
	})

	closeGlossary = () => this.setState({ glossaryModalVisible: false })

	openSubusersAll = () => this.setState({
		subusersModalVisible: true,
		subusersModalParentId: null,
		subusersModalParentName: '',
	})

	openSubusersForParent = (id: number, name: string, lastname: string) => this.setState({
		subusersModalVisible: true,
		subusersModalParentId: id,
		subusersModalParentName: `${ name || '' } ${ lastname || '' }`.trim(),
	})

	closeSubusersModal = () => this.setState({ subusersModalVisible: false })

	// ---------- Exportación CSV / Excel ----------

	toggleExportDropdown = () => this.setState((prev: any) => ({
		exportDropdownOpen: !prev.exportDropdownOpen,
	}))

	closeExportDropdown = () => this.setState({ exportDropdownOpen: false })

	exportCurrentTab = (format: 'csv' | 'excel') => {
		const { activeTab, sectionData } = this.state;
		const data: any = (sectionData as any)[activeTab];

		// Tab aún sin data -> no exportar (el botón ya está disabled, doble guarda)
		if (!data) {
			this.closeExportDropdown();
			return;
		}

		const sectionLabel = TABS.find((t) => t.key === activeTab)?.label || activeTab;
		const ctx = {
			section: activeTab,
			sectionLabel,
			filter: data.filter || { period: this.state.period },
			data: data.metrics || {},
		};

		try {
			if (format === 'csv') exportToCSV(ctx);
			else exportToExcel(ctx);
		}
		catch (e) {
			// nunca romper la UI si falla la generación del archivo
			// eslint-disable-next-line no-console
			console.log(e);
		}

		this.closeExportDropdown();
	}

	// ============ TAB USUARIOS ============
	renderUsersSection = (data: any) => {
		const m = data?.metrics || {};
		const ghost = m.ghost_users || {};
		// Corrección de naming: referral_program -> subusers
		const sub = m.subusers || {};
		const topParents = Array.isArray(sub.top_parent_users) ? sub.top_parent_users : [];

		return (
			<div className="reports-grid">
				<div className="row">
					<div className="col-md-6 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('users', 'ghost_users') } />
							<KPICard
								title="Usuarios Fantasma"
								value={ safeNum(ghost.count) }
								subtitle={ `${ formatPct(ghost.pct) } del total` }
								variant="warning" />
						</div>
					</div>
					<div className="col-md-6 reports-col">
						<div
							className="metric-card-wrap kpi-clickable"
							role="button"
							tabIndex={ 0 }
							onClick={ this.openSubusersAll }
							onKeyDown={ (e: any) => { if (e.key === 'Enter') this.openSubusersAll(); } }>
							<MetricHelp description={ metricDesc('users', 'subusers') } />
							<KPICard
								title="Subusuarios"
								value={ safeNum(sub.total_subusers) }
								subtitle={ `${ formatPct(sub.pct_subusers) } del total con sub-cuentas` }
								variant="success" />
						</div>
					</div>
				</div>
				<div className="row">
					<div className="col-md-12 reports-col">
						<ChartCard
							title="Top Cuentas con Subusuarios"
							subtitle="Usuarios que tienen perfiles secundarios"
							actions={ <MetricHelp description={ metricDesc('users', 'subusers') } /> }>
							<div className="reports-table-wrap">
								<table className="reports-table">
									<thead>
										<tr>
											<th>#</th>
											<th>Nombre</th>
											<th>Email</th>
											<th className="text-right">Subusuarios</th>
											<th className="text-right"></th>
										</tr>
									</thead>
									<tbody>
										{
											topParents.length > 0 ? (
												topParents.map((r: any, i: number) => (
													<tr
														key={ r?.id || i }
														className="row-clickable"
														onClick={ () => this.openSubusersForParent(r?.id, r?.name, r?.lastname) }>
														<td>{ i + 1 }</td>
														<td>{ this.fullName(r) }</td>
														<td>{ r?.email || '—' }</td>
														<td className="text-right">{ safeNum(r?.subusers_count) }</td>
														<td className="text-right">
															<span className="row-see">→ Ver</span>
														</td>
													</tr>
												))
											) : (
												<tr>
													<td colSpan={ 5 } className="no-items">No se encontraron registros</td>
												</tr>
											)
										}
									</tbody>
								</table>
							</div>
						</ChartCard>
					</div>
				</div>
			</div>
		)
	}

	// ============ TAB PATRIMONIO ============
	renderPatrimonySection = (data: any) => {
		const m = data?.metrics || {};
		const adopt = m.patrimony_adoption || {};
		const byPlan = m.patrimony_by_plan || {};
		const free = byPlan.free || {};
		const premium = byPlan.premium || {};
		const withP = safeNum(adopt.with_patrimony);
		const totalC = safeNum(adopt.total_clients);
		const withoutP = Math.max(totalC - withP, 0);

		return (
			<div className="reports-grid">
				<div className="row">
					<div className="col-md-5 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('patrimony', 'patrimony_adoption') } />
							<KPICard
								title="Adopción Patrimonio"
								value={ `${ withP } / ${ totalC }` }
								subtitle={ `${ formatPct(adopt.adoption_pct) } del total` }
								variant="highlight"
								size="large" />
						</div>
					</div>
					<div className="col-md-7 reports-col">
						<ChartCard
							title="Adopción Patrimonio"
							subtitle="Clientes con vs sin patrimonio"
							actions={ <MetricHelp description={ metricDesc('patrimony', 'patrimony_adoption') } /> }>
							<div className="reports-doughnut-wrap">
								<div className="reports-doughnut">
									<canvas id="chart-patrimony-adoption"></canvas>
								</div>
								<div className="reports-legend">
									<div className="legend-item">
										<span className="legend-dot" style={ { backgroundColor: COLORS.premium } }></span>
										<span className="legend-label">Con patrimonio</span>
										<span className="legend-value">{ withP }</span>
									</div>
									<div className="legend-item">
										<span className="legend-dot" style={ { backgroundColor: COLORS.muted } }></span>
										<span className="legend-label">Sin patrimonio</span>
										<span className="legend-value">{ withoutP }</span>
									</div>
								</div>
							</div>
						</ChartCard>
					</div>
				</div>
				<div className="row">
					<div className="col-md-6 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('patrimony', 'patrimony_by_plan') } />
							<KPICard
								title="Patrimonio Free"
								value={ formatMoney(free.sum_patrimony) }
								subtitle={ `${ safeNum(free.users) } usuarios · ${ formatMoney(free.avg_patrimony) } prom/usr` }
								variant="default" />
						</div>
					</div>
					<div className="col-md-6 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('patrimony', 'patrimony_by_plan') } />
							<KPICard
								title="Patrimonio Premium"
								value={ formatMoney(premium.sum_patrimony) }
								subtitle={ `${ safeNum(premium.users) } usuarios · ${ formatMoney(premium.avg_patrimony) } prom/usr` }
								variant="success" />
						</div>
					</div>
				</div>
			</div>
		)
	}

	// ============ TAB PLANES (7 métricas) ============
	renderPlansSection = (data: any) => {
		const m = data?.metrics || {};
		const conv = m.conversion_rates || {};
		const ttc = m.time_to_conversion || {};
		// funnel se dibuja como chart (canvas #chart-funnel) en renderActiveCharts
		const renewal = m.renewal_rate || {};
		const revenue = m.revenue || {};
		const pvc = m.paid_vs_complimentary || {};
		const winback = Array.isArray(m.winback_candidates) ? m.winback_candidates : [];

		return (
			<div className="reports-grid">
				{ /* Fila 1 — Revenue */ }
				<div className="row">
					<div className="col-md-4 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('plans', 'revenue') } />
							<KPICard
								title="Revenue Total Histórico"
								value={ formatMoney(revenue.lifetime_revenue) }
								subtitle={ `${ safeNum(revenue.lifetime_transactions) } transacciones` }
								variant="highlight"
								size="large" />
						</div>
					</div>
					<div className="col-md-8 reports-col">
						<ChartCard
							title="Revenue Mensual"
							subtitle="Últimos meses"
							actions={ <MetricHelp description={ metricDesc('plans', 'revenue') } /> }>
							<div className="reports-canvas-wrap">
								<canvas id="chart-revenue"></canvas>
							</div>
						</ChartCard>
					</div>
				</div>

				{ /* Fila 2 — Conversión 30/60/90 */ }
				<div className="row">
					<div className="col-md-4 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('plans', 'conversion_rates') } />
							<KPICard
								title="Conversión 30d"
								value={ safeNum(conv.conv_30d) }
								subtitle={ `${ formatPct(conv.rate_30d_pct) } de ${ safeNum(conv.total_clients) }` }
								variant="default" />
						</div>
					</div>
					<div className="col-md-4 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('plans', 'conversion_rates') } />
							<KPICard
								title="Conversión 60d"
								value={ safeNum(conv.conv_60d) }
								subtitle={ `${ formatPct(conv.rate_60d_pct) } de ${ safeNum(conv.total_clients) }` }
								variant="default" />
						</div>
					</div>
					<div className="col-md-4 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('plans', 'conversion_rates') } />
							<KPICard
								title="Conversión 90d"
								value={ safeNum(conv.conv_90d) }
								subtitle={ `${ formatPct(conv.rate_90d_pct) } de ${ safeNum(conv.total_clients) }` }
								variant="default" />
						</div>
					</div>
				</div>

				{ /* Fila 3 — Funnel + Tiempo a conversión */ }
				<div className="row">
					<div className="col-md-8 reports-col">
						<ChartCard
							title="Funnel de Conversión"
							subtitle="Registrados → Activos → Premium"
							actions={ <MetricHelp description={ metricDesc('plans', 'funnel') } /> }>
							<div className="reports-canvas-wrap">
								<canvas id="chart-funnel"></canvas>
							</div>
						</ChartCard>
					</div>
					<div className="col-md-4 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('plans', 'time_to_conversion') } />
							<KPICard
								title="Tiempo Promedio a Conversión"
								value={ `${ safeNum(ttc.avg_days) } días` }
								subtitle={ `Min ${ safeNum(ttc.min_days) } / Max ${ safeNum(ttc.max_days) } · ${ safeNum(ttc.conversions_total) } conv.` }
								variant="default" />
						</div>
					</div>
				</div>

				{ /* Fila 4 — Retención */ }
				<div className="row">
					<div className="col-md-6 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('plans', 'renewal_rate') } />
							<KPICard
								title="Tasa de Renovación"
								value={ formatPct(renewal.renewal_rate_pct) }
								subtitle={ `${ safeNum(renewal.renewed_users) } de ${ safeNum(renewal.expired_users) } vencidos` }
								variant="success" />
						</div>
					</div>
					<div className="col-md-6 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('plans', 'paid_vs_complimentary') } />
							<KPICard
								title="% Pago vs Cortesía"
								value={ formatPct(pvc.pct_real_paid) }
								subtitle={ `Pagado ${ safeNum(pvc.paid_active) } · Cortesía ${ safeNum(pvc.complimentary_active) }` }
								variant="default" />
						</div>
					</div>
				</div>

				{ /* Fila 5 — Win-back */ }
				<div className="row">
					<div className="col-md-12 reports-col">
						<ChartCard
							title="Planes Vencidos sin Renovar"
							subtitle="Candidatos a win-back"
							actions={ <MetricHelp description={ metricDesc('plans', 'winback_candidates') } /> }>
							{
								this.renderMiniTable(winback, [
									{ header: 'Nombre', render: (r) => this.fullName(r) },
									{ header: 'Email', render: (r) => r?.email || '—' },
									{
										header: 'Vencimiento',
										render: (r) => r?.expired_on ? moment(r.expired_on).format('DD/MM/YYYY') : '—'
									},
									{ header: 'Días vencido', align: 'right', render: (r) => safeNum(r?.days_expired) },
								])
							}
						</ChartCard>
					</div>
				</div>
			</div>
		)
	}

	// ============ TAB CHAT IA ============
	renderChatSection = (data: any) => {
		const m = data?.metrics || {};
		const adopt = m.chat_adoption || {};
		const post = m.post_chat_conversion || {};
		const byPlan = m.chat_by_plan || {};
		const free = byPlan.free || {};
		const premium = byPlan.premium || {};
		const ever = safeNum(adopt.ever_used);
		const totalC = safeNum(adopt.total_clients);
		const never = Math.max(totalC - ever, 0);

		return (
			<div className="reports-grid">
				<div className="row">
					<div className="col-md-5 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('chat', 'chat_adoption') } />
							<KPICard
								title="Adopción Chat IA"
								value={ `${ ever } / ${ totalC }` }
								subtitle={ `${ formatPct(adopt.adoption_pct) } del total` }
								variant="highlight"
								size="large" />
						</div>
					</div>
					<div className="col-md-7 reports-col">
						<ChartCard
							title="Adopción Chat IA"
							subtitle="Usaron vs nunca usaron"
							actions={ <MetricHelp description={ metricDesc('chat', 'chat_adoption') } /> }>
							<div className="reports-doughnut-wrap">
								<div className="reports-doughnut">
									<canvas id="chart-chat-adoption"></canvas>
								</div>
								<div className="reports-legend">
									<div className="legend-item">
										<span className="legend-dot" style={ { backgroundColor: COLORS.premium } }></span>
										<span className="legend-label">Usaron chat</span>
										<span className="legend-value">{ ever }</span>
									</div>
									<div className="legend-item">
										<span className="legend-dot" style={ { backgroundColor: COLORS.muted } }></span>
										<span className="legend-label">Nunca usaron</span>
										<span className="legend-value">{ never }</span>
									</div>
								</div>
							</div>
						</ChartCard>
					</div>
				</div>
				<div className="row">
					<div className="col-md-12 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('chat', 'post_chat_conversion') } />
							<KPICard
								title="Conversión Post-Chat"
								value={ formatPct(post.post_chat_conv_pct) }
								subtitle={ `${ safeNum(post.converted_after) } de ${ safeNum(post.free_chatters) } chatters Free compraron Premium` }
								variant="success" />
						</div>
					</div>
				</div>
				<div className="row">
					<div className="col-md-6 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('chat', 'chat_by_plan') } />
							<KPICard
								title="Uso Free"
								value={ `${ safeNum(free.chatters) } chatters` }
								subtitle={ `${ safeNum(free.messages) } mensajes · ${ safeNum(free.avg_per_user) } prom/usr` }
								variant="default" />
						</div>
					</div>
					<div className="col-md-6 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('chat', 'chat_by_plan') } />
							<KPICard
								title="Uso Premium"
								value={ `${ safeNum(premium.chatters) } chatters` }
								subtitle={ `${ safeNum(premium.messages) } mensajes · ${ safeNum(premium.avg_per_user) } prom/usr` }
								variant="success" />
						</div>
					</div>
				</div>
			</div>
		)
	}

	// ============ TAB ACTIVIDAD ============
	renderActivitySection = (data: any) => {
		const m = data?.metrics || {};
		const dwm = m.dau_wau_mau || {};
		const stick = m.stickiness || {};
		const level = String(stick.level || '').toLowerCase();
		const stickVariant: 'default' | 'success' | 'warning' | 'danger' =
			level.includes('alto') || level.includes('high') ? 'success'
			: level.includes('bajo') || level.includes('low') ? 'danger'
			: 'warning';

		return (
			<div className="reports-grid">
				<div className="row">
					<div className="col-md-4 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('activity', 'dau_wau_mau') } />
							<KPICard title="DAU" value={ safeNum(dwm.dau) } subtitle="Usuarios activos diarios" variant="default" />
						</div>
					</div>
					<div className="col-md-4 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('activity', 'dau_wau_mau') } />
							<KPICard title="WAU" value={ safeNum(dwm.wau) } subtitle="Usuarios activos semanales" variant="default" />
						</div>
					</div>
					<div className="col-md-4 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('activity', 'dau_wau_mau') } />
							<KPICard title="MAU" value={ safeNum(dwm.mau) } subtitle="Usuarios activos mensuales" variant="default" />
						</div>
					</div>
				</div>
				<div className="row">
					<div className="col-md-12 reports-col">
						<div className="metric-card-wrap">
							<MetricHelp description={ metricDesc('activity', 'stickiness') } />
							<KPICard
								title="Stickiness (DAU/MAU)"
								value={ formatPct(stick.stickiness_pct) }
								subtitle={ `Nivel: ${ stick.level || '—' }` }
								variant={ stickVariant } />
						</div>
					</div>
				</div>
				<div className="row">
					<div className="col-md-12 reports-col">
						<ChartCard
							title="Adopción por Feature"
							subtitle="Usuarios que usan cada módulo"
							actions={ <MetricHelp description={ metricDesc('activity', 'feature_adoption') } /> }>
							<div className="reports-canvas-wrap reports-canvas-tall">
								<canvas id="chart-feature-adoption"></canvas>
							</div>
						</ChartCard>
					</div>
				</div>
			</div>
		)
	}

	renderSection = (data: any) => {
		switch (this.state.activeTab) {
			case 'users': return this.renderUsersSection(data);
			case 'patrimony': return this.renderPatrimonySection(data);
			case 'plans': return this.renderPlansSection(data);
			case 'chat': return this.renderChatSection(data);
			case 'activity': return this.renderActivitySection(data);
			default: return null;
		}
	}

	renderTabContent = () => {
		const { activeTab, sectionData, loadingTabs, errorTabs } = this.state;
		const tab = TABS.find((t) => t.key === activeTab);
		const loading = loadingTabs.includes(activeTab);
		const error = errorTabs[activeTab];
		const data: any = sectionData[activeTab];

		return (
			<div className="reports-tab-content">
				<div className="reports-section-header">
					<h3>{ tab?.label }</h3>
					<div className="section-actions">
						<div
							className="export-wrapper"
							ref={ (el) => { this.exportRef = el; } }>
							<button
								className="export-btn"
								disabled={ !data }
								onClick={ this.toggleExportDropdown }>
								<span className="export-ico">⭳</span> Exportar <span className="export-caret">▾</span>
							</button>
							{
								this.state.exportDropdownOpen && (
									<div className="export-dropdown">
										<button onClick={ () => this.exportCurrentTab('csv') }>
											<span className="opt-ico">📄</span> CSV
										</button>
										<button onClick={ () => this.exportCurrentTab('excel') }>
											<span className="opt-ico">📊</span> Excel
										</button>
									</div>
								)
							}
						</div>
						<button className="glossary-trigger" onClick={ this.openGlossary }>
							¿Qué significan estas métricas?
						</button>
					</div>
				</div>

				{
					loading && (
						<div className="reports-state reports-loading">
							<span className="reports-spinner" />
							<span>Cargando…</span>
						</div>
					)
				}

				{
					!loading && error && (
						<div className="reports-state reports-error">
							<p>Error: { error }</p>
							<button
								className="reports-retry-btn"
								onClick={ () => this.loadTab(activeTab, true) }>
								Reintentar
							</button>
						</div>
					)
				}

				{
					!loading && !error && data && (
						this.renderSection(data)
					)
				}

				{
					!loading && !error && !data && (
						<div className="reports-state reports-empty">
							<p>Sin datos para mostrar.</p>
						</div>
					)
				}
			</div>
		)
	}

	render() {
		const { period, activeTab } = this.state;

		return (
			<div id="admin-reports">
				{ /* FILA 1 — Header + Selector temporal global */ }
				<div className="reports-toolbar">
					<h2 className="reports-title">Reportes</h2>
					<div className="period-pills">
						{
							PERIODS.map((p) => {
								if (p.key === 'custom') {
									return (
										<div className="period-pills-wrapper" key={ p.key }>
											<button
												className={ `period-pill ${ period === 'custom' ? 'active' : '' }` }
												onClick={ () => this.setPeriod('custom') }>
												{ p.label }
											</button>

											{
												this.state.customPopoverOpen && (
													<div
														className="custom-range-popover"
														ref={ (el) => { this.popoverRef = el; } }>
														<div className="popover-header">
															<span>Rango personalizado</span>
														</div>
														<div className="popover-body">
															<div className="custom-range-row">
																<div className="custom-range-field">
																	<DatePicker
																		label="Desde"
																		color="white"
																		placeholder="Desde"
																		value={ this.state.draftSince }
																		maxDate={ this.state.draftUntil || new Date() }
																		onChange={ (d: Date) => this.onChangeDraftDate('draftSince', d) } />
																</div>
																<div className="custom-range-field">
																	<DatePicker
																		label="Hasta"
																		color="white"
																		placeholder="Hasta"
																		value={ this.state.draftUntil }
																		minDate={ this.state.draftSince || undefined }
																		maxDate={ new Date() }
																		onChange={ (d: Date) => this.onChangeDraftDate('draftUntil', d) } />
																</div>
															</div>
															<div className="quick-ranges">
																<span className="quick-ranges-label">Atajos rápidos:</span>
																<div className="quick-ranges-pills">
																	{
																		QUICK_RANGES.map((q) => (
																			<button
																				key={ q.key }
																				className={ `quick-range-pill ${ this.state.draftQuickRange === q.key ? 'active' : '' }` }
																				onClick={ () => this.setDraftQuickRange(q.key) }>
																				{ q.label }
																			</button>
																		))
																	}
																</div>
															</div>
														</div>
														<div className="popover-footer">
															<button
																className="popover-btn popover-btn-cancel"
																onClick={ this.closeCustomPopover }>
																Cancelar
															</button>
															<button
																className="popover-btn popover-btn-apply"
																onClick={ this.applyCustomRange }>
																Aplicar
															</button>
														</div>
													</div>
												)
											}
										</div>
									)
								}

								return (
									<button
										key={ p.key }
										className={ `period-pill ${ period === p.key ? 'active' : '' }` }
										onClick={ () => this.setPeriod(p.key) }>
										{ p.label }
									</button>
								)
							})
						}
					</div>
				</div>

				{ /* FILA 2 — Tabs de sección */ }
				<div className="reports-tabs">
					{
						TABS.map((t) => (
							<button
								key={ t.key }
								className={ `reports-tab ${ activeTab === t.key ? 'active' : '' }` }
								onClick={ () => this.setActiveTab(t.key) }>
								{ t.label }
							</button>
						))
					}
				</div>

				{ /* FILA 3 — Contenido del tab activo */ }
				<div className="reports-panel">
					{ this.renderTabContent() }
				</div>

				<ModalGlossary
					visible={ this.state.glossaryModalVisible }
					section={ this.state.glossaryModalSection }
					sectionLabel={ TABS.find((t) => t.key === this.state.glossaryModalSection)?.label || '' }
					onClose={ this.closeGlossary } />

				<ModalSubusersDetail
					visible={ this.state.subusersModalVisible }
					parentUserId={ this.state.subusersModalParentId }
					parentName={ this.state.subusersModalParentName }
					onClose={ this.closeSubusersModal } />
			</div>
		)
	}
}

export default connect((state: RootState) => {
	return {
		user: state.user
	}
})(Reports);
